import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bike } from 'lucide-react'
import type { ReactNode } from 'react'

import {
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  Section,
  Spinner,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  fetchDeliveryRoutes,
  returnDeliveryRoute,
} from '../lib/api.ts'
import { clockAfter, moneyText } from '../lib/route-format.ts'

import { RouteActions } from './route-actions.tsx'

/**
 * Rotas de entrega → Na rua (F266): o que saiu hoje, com a volta prevista, e
 * o "Voltou". O acerto do dinheiro continua em Acerto do motoboy.
 */
export const RoutesOnStreet = ({
  canRoute,
}: {
  canRoute: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const routes = useQuery({
    queryKey: ['delivery-routes', 'routes', 'street'],
    queryFn: async ({ signal }) =>
      await fetchDeliveryRoutes('dispatched,returned', signal),
  })
  const back = useMutation({
    mutationFn: async (input: { id: string; version: number }) => {
      await returnDeliveryRoute(input.id, input.version)
    },
    onSuccess: async () => {
      notify.success('Rota fechada. O acerto é em Acerto do motoboy.')
      await queryClient.invalidateQueries({ queryKey: ['delivery-routes'] })
    },
  })

  const list = [...(routes.data ?? [])].sort((a, b) =>
    a.status === b.status
      ? (b.dispatchedAt ?? '').localeCompare(a.dispatchedAt ?? '')
      : a.status === 'dispatched'
        ? -1
        : 1
  )

  return (
    <Section
      title="Na rua hoje"
      description="As rotas que saíram hoje. Quando o entregador voltar, marque Voltou e faça o acerto em Acerto do motoboy."
    >
      {back.isError ? (
        <InlineAlert tone="danger">
          {back.error instanceof ApiError
            ? back.error.message
            : 'Não foi possível agora.'}
        </InlineAlert>
      ) : null}
      {routes.isPending ? (
        <Spinner label="Carregando as rotas" />
      ) : list.length === 0 ? (
        <EmptyState
          icon={<Bike aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma rota saiu hoje"
          description="As rotas despachadas em Montar saída aparecem aqui."
        />
      ) : (
        <ul className="flex flex-col gap-3" aria-label="Rotas de hoje">
          {list.map((route) => {
            const left =
              route.dispatchedAt === null ? null : new Date(route.dispatchedAt)
            return (
              <li
                key={route.id}
                className="flex flex-col gap-1 rounded-(--radius-md) border-l-4 border-l-(--border-strong) bg-(--surface-sunken) py-2 pr-3 pl-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{route.courierName}</span>
                  <Badge
                    tone={route.status === 'dispatched' ? 'info' : 'neutral'}
                  >
                    {route.status === 'dispatched' ? 'na rua' : 'voltou'}
                  </Badge>
                  <span className="text-(length:--text-xs) text-(--text-secondary)">
                    {left === null ? '' : `saiu ${clockAfter(left, 0)}`}
                    {left === null ||
                    route.returnSeconds === null ||
                    route.status !== 'dispatched'
                      ? ''
                      : ` · volta prevista ~${clockAfter(left, route.returnSeconds)}`}
                    {route.returnedAt === null
                      ? ''
                      : ` · voltou ${clockAfter(new Date(route.returnedAt), 0)}`}
                  </span>
                  {canRoute && route.status === 'dispatched' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="ml-auto"
                      disabled={back.isPending}
                      onClick={() => {
                        back.mutate({ id: route.id, version: route.version })
                      }}
                    >
                      Voltou
                    </Button>
                  ) : null}
                </div>
                <RouteActions route={route} canSend={canRoute} />
                <ol className="text-(length:--text-sm) text-(--text-secondary)">
                  {route.stops.map((stop) => (
                    <li key={stop.salesDocumentId}>
                      {stop.position}. {stop.customerName} — nº{' '}
                      {stop.number ?? '—'} · {moneyText(stop.totalAmount)}
                    </li>
                  ))}
                </ol>
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}
